/**
 * Location Stock Service
 * 
 * Manages stock levels at warehouse locations.
 * Updates location stock quantities and utilisation percentages
 * after stock movements (receive, pick, transfer, adjust).
 */

import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { getInitializedLocations } from "@/data/initializeLocationStock";

// Get locations with actual stock data
const getMockLocations = () => getInitializedLocations();
const mockLocations = getMockLocations();

export interface StockMovement {
  locationCode: string;
  quantityChange: number; // Positive for additions, negative for removals
  movementType: 'receive' | 'pick' | 'transfer-in' | 'transfer-out' | 'adjust' | 'write-off';
  referenceNumber?: string;
  notes?: string;
}

/**
 * Update stock level at a specific location
 */
export const updateLocationStock = (
  locationCode: string,
  quantityChange: number
): boolean => {
  const location = mockLocations.find(l => l.locationCode === locationCode);
  
  if (!location) {
    console.error(`Location ${locationCode} not found`);
    return false;
  }

  // Update current stock
  const newStock = location.currentStock + quantityChange;
  
  // Prevent negative stock
  if (newStock < 0) {
    console.error(`Cannot reduce stock below zero at location ${locationCode}`);
    return false;
  }

  // Check capacity if adding stock
  if (quantityChange > 0 && location.capacity && newStock > location.capacity) {
    console.warn(`Location ${locationCode} exceeds capacity: ${newStock} > ${location.capacity}`);
    // Allow it but log warning
  }

  // Update stock and utilisation
  location.currentStock = newStock;
  
  if (location.capacity) {
    location.utilizationPercent = Math.round((newStock / location.capacity) * 100);
  }

  return true;
};

/**
 * Process a stock movement (can involve multiple locations)
 */
export const processStockMovement = (movement: StockMovement): boolean => {
  return updateLocationStock(movement.locationCode, movement.quantityChange);
};

/**
 * Process a transfer between two locations
 */
export const processTransfer = (
  fromLocationCode: string,
  toLocationCode: string,
  quantity: number
): boolean => {
  // Remove from source location
  const removeSuccess = updateLocationStock(fromLocationCode, -quantity);
  if (!removeSuccess) {
    return false;
  }

  // Add to destination location
  const addSuccess = updateLocationStock(toLocationCode, quantity);
  if (!addSuccess) {
    // Rollback the removal
    updateLocationStock(fromLocationCode, quantity);
    return false;
  }

  return true;
};

/**
 * Get available capacity at a location
 */
export const getAvailableCapacity = (locationCode: string): number | null => {
  const location = mockLocations.find(l => l.locationCode === locationCode);
  
  if (!location || !location.capacity) {
    return null;
  }

  return location.capacity - location.currentStock;
};

/**
 * Check if a location can accommodate a quantity
 */
export const canAccommodate = (locationCode: string, quantity: number): boolean => {
  const availableCapacity = getAvailableCapacity(locationCode);
  
  // If no capacity limit, always return true
  if (availableCapacity === null) {
    return true;
  }

  return quantity <= availableCapacity;
};

/**
 * Get current stock at a location
 */
export const getLocationStock = (locationCode: string): number => {
  const location = mockLocations.find(l => l.locationCode === locationCode);
  return location?.currentStock || 0;
};

/**
 * Get all locations for a warehouse
 */
export const getWarehouseLocations = (warehouseCode: string) => {
  return mockLocations.filter(l => l.warehouseCode === warehouseCode && l.active);
};

/**
 * Get locations by type
 */
export const getLocationsByType = (
  warehouseCode: string,
  locationType: string
) => {
  return mockLocations.filter(
    l => l.warehouseCode === warehouseCode && 
         l.locationType === locationType && 
         l.active
  );
};

/**
 * Get default receive location for a warehouse
 */
export const getDefaultReceiveLocation = (warehouseCode: string): string | null => {
  const warehouse = mockWarehouses.find(w => w.code === warehouseCode);
  return warehouse?.defaultReceiveLocation || null;
};

/**
 * Get default pick location for a warehouse
 */
export const getDefaultPickLocation = (warehouseCode: string): string | null => {
  const warehouse = mockWarehouses.find(w => w.code === warehouseCode);
  return warehouse?.defaultPickLocation || null;
};

/**
 * Get locations sorted by picking sequence
 */
export const getPickingLocations = (warehouseCode: string) => {
  return mockLocations
    .filter(l => 
      l.warehouseCode === warehouseCode && 
      l.locationType === 'Picking' && 
      l.active &&
      l.currentStock > 0
    )
    .sort((a, b) => (a.pickingSequence || 999) - (b.pickingSequence || 999));
};

/**
 * Update warehouse utilisation based on location stocks
 */
export const updateWarehouseUtilization = (warehouseCode: string): void => {
  const warehouse = mockWarehouses.find(w => w.code === warehouseCode);
  if (!warehouse || !warehouse.totalCapacity) {
    return;
  }

  const warehouseLocations = getWarehouseLocations(warehouseCode);
  const totalStock = warehouseLocations.reduce((sum, loc) => sum + loc.currentStock, 0);
  
  warehouse.currentUtilization = Math.round((totalStock / warehouse.totalCapacity) * 100);
};

/**
 * Validate location exists and is active
 */
export const validateLocation = (locationCode: string): { valid: boolean; error?: string } => {
  const location = mockLocations.find(l => l.locationCode === locationCode);
  
  if (!location) {
    return { valid: false, error: `Location ${locationCode} not found` };
  }

  if (!location.active) {
    return { valid: false, error: `Location ${locationCode} is inactive` };
  }

  return { valid: true };
};

/**
 * Get location details
 */
export const getLocationDetails = (locationCode: string) => {
  return mockLocations.find(l => l.locationCode === locationCode);
};

/**
 * Format location code for display
 */
export const formatLocationCode = (locationCode: string): string => {
  const location = mockLocations.find(l => l.locationCode === locationCode);
  if (!location) return locationCode;

  const parts = [];
  if (location.aisle) parts.push(`A${location.aisle}`);
  if (location.rack) parts.push(`R${location.rack}`);
  if (location.shelf) parts.push(`S${location.shelf}`);
  if (location.bin) parts.push(`B${location.bin}`);

  return parts.length > 0 ? parts.join('-') : locationCode;
};
